import React from 'react'
import { motion } from 'framer-motion'
import Header from '../components/Header'
import Footer from '../components/Footer'

const Terms = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
            <p className="text-lg text-gray-600 mb-8">
              Last updated: {new Date().toLocaleDateString()}
            </p>

            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 mb-4">
                  By accessing or using ProfessionalSignature.com ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
                  If you disagree with any part of these terms, then you may not access the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
                <p className="text-gray-700 mb-4">
                  ProfessionalSignature.com is an AI-powered signature generation service that creates personalized, 
                  professional handwritten signatures. Our service includes:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>AI-generated signature creation in multiple styles</li>
                  <li>High-resolution signature file downloads</li>
                  <li>Multiple file formats (PNG, JPG, SVG)</li>
                  <li>Commercial usage rights for purchased signatures</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts and Eligibility</h2>
                <p className="text-gray-700 mb-4">
                  To use our paid services, you must:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Be at least 18 years of age</li>
                  <li>Provide accurate and complete information</li>
                  <li>Have the legal capacity to enter into binding contracts</li>
                  <li>Not be prohibited from using the service under applicable laws</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Payment Terms</h2>
                <p className="text-gray-700 mb-4">
                  Our signature generation service is provided on a pay-per-use basis:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Preview generation is provided to help you choose your preferred signature</li>
                  <li>Payment of €14.95 is required to download high-resolution files</li>
                  <li>All payments are processed securely through Stripe</li>
                  <li>Prices are subject to change with notice</li>
                  <li>All sales are final - no refunds except as required by law</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. License and Usage Rights</h2>
                <p className="text-gray-700 mb-4">
                  Upon successful payment, you receive:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>A non-exclusive, worldwide license to use your purchased signature</li>
                  <li>Commercial usage rights for business and professional purposes</li>
                  <li>The right to modify and adapt the signature for your needs</li>
                  <li>Permission to use the signature in digital and print materials</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  You may NOT:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Resell or redistribute the signature files</li>
                  <li>Use the signature to impersonate others</li>
                  <li>Use the signature for illegal or fraudulent purposes</li>
                  <li>Claim ownership of the underlying AI technology</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. User Responsibilities</h2>
                <p className="text-gray-700 mb-4">
                  You are responsible for:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Providing accurate information for signature generation</li>
                  <li>Using the service only for lawful purposes</li>
                  <li>Protecting your downloaded signature files</li>
                  <li>Complying with all applicable laws and regulations</li>
                  <li>Not attempting to reverse engineer or circumvent our systems</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
                <p className="text-gray-700 mb-4">
                  The Service and its original content, features, and functionality are and will remain the exclusive 
                  property of ProfessionalSignature.com and its licensors. The service is protected by copyright, 
                  trademark, and other laws.
                </p>
                <p className="text-gray-700 mb-4">
                  You retain ownership of any personal information you provide, and upon purchase, you receive 
                  the specified usage rights to your generated signature.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Privacy and Data Protection</h2>
                <p className="text-gray-700 mb-4">
                  Your privacy is important to us. Please review our Privacy Policy, which also governs your use 
                  of the Service, to understand our practices.
                </p>
                <p className="text-gray-700 mb-4">
                  We collect and process personal information in accordance with applicable data protection laws, 
                  including GDPR where applicable.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Service Availability</h2>
                <p className="text-gray-700 mb-4">
                  We strive to maintain high service availability, but we do not guarantee uninterrupted access. 
                  We may temporarily suspend or restrict access for maintenance, updates, or technical issues.
                </p>
                <p className="text-gray-700 mb-4">
                  We reserve the right to modify, suspend, or discontinue the service at any time with reasonable notice.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Disclaimers and Limitations</h2>
                <p className="text-gray-700 mb-4">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. 
                  WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>Merchantability and fitness for a particular purpose</li>
                  <li>Non-infringement of third-party rights</li>
                  <li>Accuracy or completeness of generated signatures</li>
                  <li>Uninterrupted or error-free operation</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  OUR LIABILITY IS LIMITED TO THE AMOUNT YOU PAID FOR THE SERVICE. WE ARE NOT LIABLE FOR 
                  INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Refund Policy</h2>
                <p className="text-gray-700 mb-4">
                  Due to the digital nature of our service and immediate delivery of files:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4">
                  <li>All sales are generally final</li>
                  <li>Refunds may be considered for technical failures preventing file delivery</li>
                  <li>Quality concerns should be reported within 24 hours of purchase</li>
                  <li>Refunds will be processed through the original payment method</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Termination</h2>
                <p className="text-gray-700 mb-4">
                  We may terminate or suspend your access immediately, without prior notice, for any reason, 
                  including breach of these Terms. Upon termination, your right to use the Service ceases immediately.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
                <p className="text-gray-700 mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], 
                  without regard to its conflict of law provisions. Any disputes shall be resolved in the competent 
                  courts of [Your Jurisdiction].
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Changes to Terms</h2>
                <p className="text-gray-700 mb-4">
                  We reserve the right to modify or replace these Terms at any time. If a revision is material, 
                  we will try to provide at least 30 days notice prior to any new terms taking effect.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Contact Information</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <ul className="list-none text-gray-700">
                  <li>Email: <a href="mailto:info@professionalsignature.com?subject=Terms of Service" className="text-primary-600 hover:text-primary-700">info@professionalsignature.com</a></li>
                  <li>Website: https://professionalsignature.com/contact</li>
                </ul>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Terms
